import { ChangeDetectionStrategy, Component, computed, inject, input, model, signal } from '@angular/core';
import { FileUploadService, IPreparedFile } from '../../../core/services/file-upload.service';
import { FilePurpose } from '../../../core/models/file.model';
import { fileExt, fileSize } from '../../../core/utils/format.util';
import { IDropzoneFile } from '../dropzone/dropzone.component';

export interface ILogoValue {
  idFile: string | null;
  url: string | null;
  name?: string;
  meta?: string;
  /** Archivo listo para subir cuando el negocio todavía no existe (Agregar negocio). */
  prepared?: IPreparedFile | null;
}

/**
 * Logo cuadrado (512×512) u horizontal (1200×400). Ajusta la imagen al tamaño exacto que pide la API.
 * Con `idCompany` sube de inmediato (presign + PUT + confirm); sin él, guarda el archivo para subirlo al crear.
 */
@Component({
  selector: 'app-logo-picker',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <app-dropzone
      [accept]="'image/png,image/jpeg'"
      [title]="horizontal() ? 'Sube tu logo horizontal' : 'Sube tu logo cuadrado'"
      [hint]="horizontal() ? 'PNG · 3:1 · 1200×400 px' : 'PNG · 1:1 · 512×512 px'"
      [compact]="true"
      [file]="dzFile()"
      [progress]="progress()"
      [error]="error()"
      [disabled]="disabled()"
      (selected)="pick($event)"
      (removed)="remove()" />
  `,
})
export class LogoPickerComponent {
  private uploader = inject(FileUploadService);

  readonly purpose = input.required<FilePurpose>();
  readonly idCompany = input<string | null>(null);
  readonly companyName = input<string>('');
  /** Sin subir ni quitar logo (p. ej. negocio inactivo en solo lectura). */
  readonly disabled = input<boolean>(false);
  readonly value = model<ILogoValue>({ idFile: null, url: null });

  readonly progress = signal<number | null>(null);
  readonly error = signal<string | null>(null);
  readonly horizontal = computed(() => this.purpose() === 'logo_horizontal');

  readonly dzFile = computed<IDropzoneFile | null>(() => {
    const v = this.value();
    if (!v.url) return null;
    return {
      name: v.name || (this.horizontal() ? 'logo-horizontal.png' : 'logo-cuadrado.png'),
      meta: v.meta || (this.horizontal() ? 'PNG · 1200×400' : 'PNG · 512×512'),
      previewUrl: v.url,
      format: this.horizontal() ? 'horizontal' : 'square',
      verified: true,
    };
  });

  async pick(file: File): Promise<void> {
    if (this.disabled()) return;
    this.error.set(null);
    const res = await this.uploader.prepare(file, this.purpose());
    if (!res.ok) { this.error.set(res.message); return; }
    const p = res.prepared;
    const meta = `${fileSize(p.blob.size)} · ${fileExt(p.mimeType)} · ${p.width}×${p.height}`;
    const id = this.idCompany();
    if (!id) {
      this.value.set({ idFile: null, url: p.previewUrl, name: file.name, meta, prepared: p });
      return;
    }
    this.value.set({ idFile: null, url: p.previewUrl, name: file.name, meta });
    this.progress.set(0);
    const up = await this.uploader.upload(p, this.purpose(), id, pct => this.progress.set(pct));
    this.progress.set(null);
    if (!up.ok || !up.file) {
      this.error.set(up.message || 'No pudimos subir el logo.');
      this.value.set({ idFile: null, url: null });
      return;
    }
    this.value.set({ idFile: up.file.idFile, url: p.previewUrl, name: file.name, meta });
  }

  remove(): void {
    if (this.disabled()) return;
    this.error.set(null);
    this.progress.set(null);
    this.value.set({ idFile: null, url: null });
  }
}
